/**
 * Workspace Context Provider
 * Manages workspace selection. No dependency on ProjectContext.
 *
 * boardId/setBoardId have been moved to ProjectContext (they are project-scoped).
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { workspacesApi, type Workspace } from '../api/workspaces';

// Storage keys
const WORKSPACE_STORAGE_KEY = 'martha.workspace.selected';

interface WorkspaceContextValue {
  // Workspace management
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  isLoadingWorkspaces: boolean;
  workspaceError: string | null;
  setCurrentWorkspaceId: (id: string) => void;
  setCurrentWorkspaceBySlug: (slug: string) => boolean;
  refreshWorkspaces: () => Promise<void>;

  /** Derived slug from currentWorkspace (e.g. "archie", "martha") */
  wsSlug: string | null;

  // Loading state
  isLoading: boolean;
  error: string | null;

  // Legacy compatibility
  /** @deprecated Use projectId from useProject() instead */
  worktreeId: string;
  /** @deprecated Worktrees are no longer used */
  worktree: null;
  /** @deprecated Worktrees are no longer used */
  worktrees: never[];
  /** @deprecated Worktrees are no longer used */
  allWorktrees: never[];
  /** @deprecated No-op, worktrees are deprecated */
  setWorktreeId: (id: string) => void;
  /** @deprecated Use refreshWorkspaces instead */
  refreshWorktrees: () => Promise<void>;

  // Legacy — kept so consumers that destructure these don't break
  /** @deprecated Use useProject().project?.id instead */
  projectId: string | null;
  /** @deprecated Moved to ProjectContext */
  boardId: string | null;
  /** @deprecated Moved to ProjectContext */
  setBoardId: (id: string | null) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Workspace state
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspaceId, setCurrentWorkspaceIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(WORKSPACE_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(true);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);

  // Compute current workspace from ID
  const currentWorkspace = workspaces.find(w => w.id === currentWorkspaceId) || workspaces[0] || null;

  // Derived slug
  const wsSlug = currentWorkspace?.slug ?? null;

  // Use a ref for currentWorkspaceId inside fetchWorkspaces to avoid
  // recreating the callback (and re-running the effect) on every ID change.
  const currentWorkspaceIdRef = useRef(currentWorkspaceId);
  currentWorkspaceIdRef.current = currentWorkspaceId;

  // Fetch workspaces on mount (stable callback — no deps that change)
  const fetchWorkspaces = useCallback(async () => {
    setIsLoadingWorkspaces(true);
    setWorkspaceError(null);
    try {
      const data = await workspacesApi.list();
      setWorkspaces(data);

      // If no workspace is selected but we have workspaces, select the first one
      if (!currentWorkspaceIdRef.current && data.length > 0) {
        setCurrentWorkspaceIdState(data[0].id);
        try {
          localStorage.setItem(WORKSPACE_STORAGE_KEY, data[0].id);
        } catch (err) {
          console.error('Failed to persist workspace selection:', err);
        }
        // Notify parent ProjectProvider
        window.dispatchEvent(new CustomEvent('workspace-changed', { detail: data[0].id }));
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
      setWorkspaceError(err instanceof Error ? err.message : 'Failed to fetch workspaces');
    } finally {
      setIsLoadingWorkspaces(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  // Set workspace ID and persist to localStorage
  const setCurrentWorkspaceId = useCallback((id: string) => {
    setCurrentWorkspaceIdState(id);
    try {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, id);
    } catch (err) {
      console.error('Failed to persist workspace selection:', err);
    }
    // Notify ProjectProvider (same-tab event since StorageEvent only fires cross-tab)
    window.dispatchEvent(new CustomEvent('workspace-changed', { detail: id }));
  }, []);

  // Refs for setCurrentWorkspaceBySlug to avoid recreating callback on every render
  const workspacesRef = useRef(workspaces);
  workspacesRef.current = workspaces;

  // Set workspace by slug — returns true if found (stable callback)
  const setCurrentWorkspaceBySlug = useCallback((slug: string): boolean => {
    const ws = workspacesRef.current.find(w => w.slug === slug);
    if (ws) {
      if (ws.id !== currentWorkspaceIdRef.current) {
        setCurrentWorkspaceId(ws.id);
      }
      return true;
    }
    return false;
  }, [setCurrentWorkspaceId]);

  // Listen for storage events (multi-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === WORKSPACE_STORAGE_KEY) {
        setCurrentWorkspaceIdState(e.newValue);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Legacy no-op functions
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setWorktreeId = useCallback((_id: string) => {
    console.warn('[WorkspaceContext] setWorktreeId is deprecated. Use ProjectContext instead.');
  }, []);

  const refreshWorktrees = useCallback(async () => {
    console.warn('[WorkspaceContext] refreshWorktrees is deprecated. Use refreshWorkspaces instead.');
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  const value = useMemo<WorkspaceContextValue>(() => ({
    // Workspace management
    currentWorkspace,
    workspaces,
    isLoadingWorkspaces,
    workspaceError,
    setCurrentWorkspaceId,
    setCurrentWorkspaceBySlug,
    refreshWorkspaces: fetchWorkspaces,
    wsSlug,

    isLoading: isLoadingWorkspaces,
    error: workspaceError,

    // Legacy compatibility (deprecated)
    worktreeId: '',
    worktree: null,
    worktrees: [],
    allWorktrees: [],
    setWorktreeId,
    refreshWorktrees,

    // Legacy stubs — boardId moved to ProjectContext, projectId should come from useProject()
    projectId: null,
    boardId: null,
    setBoardId: () => {},
  }), [
    currentWorkspace, workspaces, isLoadingWorkspaces, workspaceError,
    setCurrentWorkspaceId, setCurrentWorkspaceBySlug, fetchWorkspaces, wsSlug,
    setWorktreeId, refreshWorktrees,
  ]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

/**
 * Hook to access the workspace context
 */
export const useWorkspace = (): WorkspaceContextValue => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

/**
 * Hook to get just the project ID
 * @deprecated Use useProject().project?.id instead
 */
export const useWorktreeId = (): string => {
  return '';
};
